import { useState, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import {
  Search,
  Building,
  User,
  ArrowRight,
  Network,
  LayoutGrid,
  List,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Timeline } from '@/components/ui/Timeline';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import { SentimentIndicator } from '@/components/modules/SentimentIndicator';
import { ContextGraph } from '@/components/modules/ContextGraph';
import type { GraphNode, GraphEdge } from '@/components/modules/ContextGraph';
import { useContextStore } from '@/stores/contextStore';

interface Entity {
  id: string;
  type: 'contact' | 'company';
  name: string;
  email?: string;
  sentiment?: string;
  contactType?: string;
  totalInteractions: number;
}

interface Connection {
  id: string;
  entity: Entity;
  edgeType: string;
  weight: number;
}

const edgeColors: Record<string, string> = {
  works_at: 'text-blue-400',
  manages: 'text-purple-400',
  referred_by: 'text-green-400',
  partner_of: 'text-amber-400',
  vendor_for: 'text-orange-400',
  customer_of: 'text-emerald-400',
  related_to: 'text-surface-400',
};

type ViewMode = 'graph' | 'list';

export function ContextExplorer() {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('graph');
  const {
    contacts,
    companies,
    edges,
    interactions,
    selectedEntity,
    isLoading,
    error,
    fetchEntities,
    selectEntity,
    clearError,
  } = useContextStore();

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  // Map contacts + companies into unified Entity list
  const entities: Entity[] = useMemo(() => {
    const contactEntities: Entity[] = contacts.map((c) => ({
      id: c.id,
      type: 'contact' as const,
      name: c.name,
      email: c.email,
      sentiment: c.sentiment,
      contactType: c.type,
      totalInteractions: c.totalInteractions ?? 0,
    }));
    const companyEntities: Entity[] = companies.map((c) => ({
      id: c.id,
      type: 'company' as const,
      name: c.name,
      totalInteractions: 0,
    }));
    return [...contactEntities, ...companyEntities];
  }, [contacts, companies]);

  // Client-side filter on name/email for responsiveness
  const filteredEntities = useMemo(
    () =>
      entities.filter(
        (e) =>
          !search ||
          e.name.toLowerCase().includes(search.toLowerCase()) ||
          e.email?.toLowerCase().includes(search.toLowerCase()),
      ),
    [entities, search],
  );

  // Build graph data from entities + edges
  const graphNodes: GraphNode[] = useMemo(
    () =>
      filteredEntities.map((e) => ({
        id: e.id,
        type: e.type,
        label: e.name,
        subtype: e.type === 'contact' ? (e.contactType ?? 'other') : 'company',
        interactions: e.totalInteractions,
      })),
    [filteredEntities],
  );

  const graphEdges: GraphEdge[] = useMemo(() => {
    const nodeIds = new Set(filteredEntities.map((e) => e.id));
    return edges
      .filter((e) => nodeIds.has(e.sourceId) && nodeIds.has(e.targetId))
      .map((e) => ({
        source: e.sourceId,
        target: e.targetId,
        type: e.edgeType,
        weight: e.weight ?? 1,
      }));
  }, [edges, filteredEntities]);

  // Resolve edges into connections with entity details
  const connections: Connection[] = useMemo(() => {
    if (!selectedEntity) return [];
    return edges
      .map((edge) => {
        const isSource =
          edge.sourceType === selectedEntity.type && edge.sourceId === selectedEntity.id;
        const targetType = isSource ? edge.targetType : edge.sourceType;
        const targetId = isSource ? edge.targetId : edge.sourceId;
        const found = entities.find((e) => e.id === targetId && e.type === targetType);
        if (!found) return null;
        return {
          id: edge.id,
          entity: found,
          edgeType: edge.edgeType,
          weight: edge.weight ?? 1,
        };
      })
      .filter(Boolean) as Connection[];
  }, [edges, selectedEntity, entities]);

  // Map interactions to Timeline format
  const timelineItems = useMemo(
    () =>
      interactions.map((i) => ({
        id: i.id,
        type: i.type ?? i.channel ?? 'event',
        title: i.subject || i.type || 'Interaction',
        summary: i.summary || '',
        timestamp: i.createdAt,
      })),
    [interactions],
  );

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      fetchEntities(search || undefined);
    }
  };

  const handleSelectEntity = (entity: Entity) => {
    selectEntity({
      type: entity.type,
      id: entity.id,
      name: entity.name,
      email: entity.email,
      sentiment: entity.sentiment,
    });
  };

  const handleGraphSelect = (id: string, type: 'contact' | 'company') => {
    const entity = entities.find((e) => e.id === id && e.type === type);
    if (entity) handleSelectEntity(entity);
  };

  // ---------------------------------------------------------------------------
  // Loading skeleton
  // ---------------------------------------------------------------------------
  if (isLoading && entities.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        <div>
          <Skeleton className="h-6 w-40 mb-2" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-10 max-w-md rounded-lg" />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16 mb-2" />
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
          <div className="lg:col-span-3">
            <div className="bg-surface-800 border border-surface-700 rounded-lg p-12 flex items-center justify-center">
              <div className="text-center space-y-3">
                <Skeleton className="h-10 w-10 rounded-full mx-auto" />
                <Skeleton className="h-4 w-56 mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Detail panel (shared between graph & list views)
  // ---------------------------------------------------------------------------
  const detailPanel = selectedEntity ? (
    <div className="space-y-6">
      {/* Entity header */}
      <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-surface-700 rounded-lg">
            {selectedEntity.type === 'company' ? (
              <Building className="h-6 w-6 text-blue-400" />
            ) : (
              <User className="h-6 w-6 text-purple-400" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-surface-100">{selectedEntity.name}</h3>
            <div className="flex items-center gap-3 mt-1">
              <Badge variant={selectedEntity.type === 'company' ? 'info' : 'purple'}>
                {selectedEntity.type}
              </Badge>
              {selectedEntity.email && (
                <span className="text-sm text-surface-400">{selectedEntity.email}</span>
              )}
              {selectedEntity.sentiment && (
                <SentimentIndicator sentiment={selectedEntity.sentiment} showLabel />
              )}
            </div>
            <p className="text-xs text-surface-500 mt-2">
              {interactions.length} interactions loaded
            </p>
          </div>
        </div>
      </div>

      {/* Connections */}
      <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <Network className="h-4 w-4 text-nexus-400" />
          <h3 className="text-sm font-semibold text-surface-200">Connected Entities</h3>
        </div>
        <div className="space-y-2">
          {connections.length === 0 ? (
            <p className="text-sm text-surface-500 py-4 text-center">No connections found</p>
          ) : (
            connections.map((conn) => (
              <button
                key={conn.id}
                onClick={() => handleSelectEntity(conn.entity)}
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-surface-900/50 rounded-lg border border-surface-700/50 hover:border-surface-600 transition-colors text-left cursor-pointer"
              >
                {conn.entity.type === 'company' ? (
                  <Building className="h-4 w-4 text-blue-400 flex-shrink-0" />
                ) : (
                  <User className="h-4 w-4 text-purple-400 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-surface-200 truncate">
                    {conn.entity.name}
                  </p>
                  <p className={`text-xs ${edgeColors[conn.edgeType] || 'text-surface-500'}`}>
                    {conn.edgeType.replace('_', ' ')}
                  </p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-surface-600" />
              </button>
            ))
          )}
        </div>
      </div>

      {/* Interaction timeline */}
      <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-surface-200 mb-4">Recent Interactions</h3>
        {timelineItems.length > 0 ? (
          <Timeline items={timelineItems} />
        ) : (
          <p className="text-sm text-surface-500 py-4 text-center">No interactions recorded</p>
        )}
      </div>
    </div>
  ) : null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6 max-w-[1440px]">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-surface-100">Context Explorer</h2>
        <p className="text-sm text-surface-400 mt-0.5">
          Navigate relationships and interaction history across your network
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-400">{error}</p>
          <button
            onClick={clearError}
            className="text-xs text-red-400 hover:text-red-300 cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Toolbar: search + view toggle */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-surface-500" />
          <input
            type="text"
            placeholder="Search contacts, companies... (Enter to search API)"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="w-full pl-10 pr-4 py-2.5 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-200 placeholder-surface-500 focus:outline-none focus:ring-1 focus:ring-nexus-500"
          />
        </div>
        {/* View toggle */}
        <div className="flex items-center bg-surface-800 border border-surface-700 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('graph')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              viewMode === 'graph'
                ? 'bg-nexus-600/20 text-nexus-400'
                : 'text-surface-400 hover:text-surface-200'
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Graph
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              viewMode === 'list'
                ? 'bg-nexus-600/20 text-nexus-400'
                : 'text-surface-400 hover:text-surface-200'
            }`}
          >
            <List className="h-3.5 w-3.5" />
            List
          </button>
        </div>
      </div>

      {/* ================================================================= */}
      {/* GRAPH VIEW                                                        */}
      {/* ================================================================= */}
      {viewMode === 'graph' && (
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          {/* Graph canvas — full width on mobile, 70% on desktop */}
          <div className="lg:col-span-7 h-[360px] md:h-[520px]">
            {filteredEntities.length === 0 ? (
              <div className="flex items-center justify-center h-full bg-surface-800 border border-surface-700 rounded-lg">
                <div className="text-center">
                  <Network className="h-10 w-10 text-surface-600 mx-auto mb-3" />
                  <p className="text-surface-400">No entities to display</p>
                </div>
              </div>
            ) : (
              <ContextGraph
                nodes={graphNodes}
                edges={graphEdges}
                selectedId={selectedEntity?.id}
                onSelect={handleGraphSelect}
              />
            )}
          </div>
          {/* Detail panel — hidden on mobile unless a node is selected */}
          <div className={clsx(
            'lg:col-span-3 max-h-[520px] overflow-y-auto',
            selectedEntity ? 'block' : 'hidden lg:block',
          )}>
            {detailPanel ?? (
              <div className="flex items-center justify-center h-full bg-surface-800 border border-surface-700 rounded-lg p-8">
                <div className="text-center">
                  <Network className="h-10 w-10 text-surface-600 mx-auto mb-3" />
                  <p className="text-surface-400 text-sm">
                    Click a node to explore its context
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* LIST VIEW (original layout)                                       */}
      {/* ================================================================= */}
      {viewMode === 'list' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Entity list */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-surface-300 px-1">Entities</h3>
            {filteredEntities.length === 0 ? (
              <p className="text-sm text-surface-500 px-1 py-4">No entities found</p>
            ) : (
              filteredEntities.map((entity) => (
                <button
                  key={`${entity.type}-${entity.id}`}
                  onClick={() => handleSelectEntity(entity)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors cursor-pointer border ${
                    selectedEntity?.id === entity.id && selectedEntity?.type === entity.type
                      ? 'bg-nexus-600/10 border-nexus-500/30 text-nexus-400'
                      : 'bg-surface-800 border-surface-700 text-surface-300 hover:border-surface-600'
                  }`}
                >
                  {entity.type === 'company' ? (
                    <Building className="h-4 w-4 flex-shrink-0 text-blue-400" />
                  ) : (
                    <User className="h-4 w-4 flex-shrink-0 text-purple-400" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{entity.name}</p>
                    <p className="text-[10px] text-surface-500 capitalize">{entity.type}</p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Entity detail */}
          {selectedEntity ? (
            <div className="lg:col-span-3">{detailPanel}</div>
          ) : (
            <div className="lg:col-span-3 flex items-center justify-center bg-surface-800 border border-surface-700 rounded-lg p-12">
              <div className="text-center">
                <Network className="h-10 w-10 text-surface-600 mx-auto mb-3" />
                <p className="text-surface-400">Select an entity to explore its context</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
